import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, queryString } from "../api.js";
import { ErrorBox, Pager, SuccessBox, fmtRelative, shortHwid, VerdictPill } from "../components/helpers.jsx";

const LIMIT = 50;

function StatusPill({ status, endReason }) {
    const map = {
        active:  "allow",
        ended:   "lifted",
        expired: "deny",      // heartbeat / lifetime timeout — distinct from clean close
        banned:  "ban",
        denied:  "deny",
    };
    // expired + heartbeat_timeout deserves louder labelling — that's the
    // loader-thread-frozen / network-stalled case we tightened the sweep
    // window for.
    const label = (status === "expired" && endReason === "heartbeat_timeout")
        ? "timed out"
        : status;
    return <span className={`pill ${map[status] ?? "lifted"}`}>{label}</span>;
}

export default function Sessions() {
    const [data, setData] = useState({ rows: [], total: 0 });
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [offset, setOffset] = useState(0);
    const [err, setErr] = useState(null);
    const [msg, setMsg] = useState(null);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let alive = true;
        api.get("/api/admin/sessions" + queryString({ q, status, limit: LIMIT, offset }))
            .then((d) => { if (alive) setData(d); })
            .catch((e) => { if (alive) setErr(e.message); });
        return () => { alive = false; };
    }, [q, status, offset, reload]);

    // Auto-refresh active view every 10s so beats show up live.
    useEffect(() => {
        if (status && status !== "active") return;
        const t = setInterval(() => setReload(Date.now()), 10_000);
        return () => clearInterval(t);
    }, [status]);

    async function forceEnd(id) {
        if (!confirm(`Force-end session #${id}? The loader will lose its heartbeat target.`)) return;
        setErr(null); setMsg(null);
        try {
            await api.post(`/api/admin/sessions/${id}/end`);
            setMsg(`Session #${id} force-ended.`);
            setReload(Date.now());
        } catch (e) { setErr(e.message); }
    }

    return (
        <>
            <div className="page-header">
                <div>
                    <h1>Sessions</h1>
                    <div className="sub">Live loader sessions and recent history. Active rows refresh every 10s.</div>
                </div>
            </div>

            <ErrorBox>{err}</ErrorBox>
            <SuccessBox>{msg}</SuccessBox>

            <div className="table-wrap">
                <div className="table-toolbar">
                    <input placeholder="Search subject / hwid / ip / token…" value={q}
                           onChange={(e) => { setOffset(0); setQ(e.target.value); }} />
                    <select value={status} onChange={(e) => { setOffset(0); setStatus(e.target.value); }}>
                        <option value="">all statuses</option>
                        <option value="active">active</option>
                        <option value="ended">ended</option>
                        <option value="expired">expired</option>
                        <option value="banned">banned</option>
                        <option value="denied">denied</option>
                    </select>
                </div>
                <table className="data">
                    <thead>
                        <tr>
                            <th>Started</th>
                            <th>Status</th>
                            <th>Subject</th>
                            <th>HWID</th>
                            <th>Beats</th>
                            <th>Last verdict</th>
                            <th>Last beat</th>
                            <th>IP</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.length === 0 && (
                            <tr><td colSpan={9} className="text-dim" style={{ padding: 24, textAlign: "center" }}>
                                No sessions yet
                            </td></tr>
                        )}
                        {data.rows.map((r) => (
                            <tr key={r.id}>
                                <td title={new Date(r.createdAt).toLocaleString()}>
                                    <Link to={`/sessions/${r.id}`}>{fmtRelative(r.createdAt)}</Link>
                                </td>
                                <td>
                                    <StatusPill status={r.status} endReason={r.endReason} />
                                    {r.endReason && r.status !== "active" && (
                                        <div className="text-dim" style={{ fontSize: 10, marginTop: 2 }}>
                                            {r.endReason}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {r.subjectLabel && (
                                        <div style={{ fontWeight: 500 }}>{r.subjectLabel}</div>
                                    )}
                                    <div className="mono text-dim" style={{ fontSize: 11 }}>{r.subject}</div>
                                </td>
                                <td className="mono" title={r.hwid ?? ""}>{shortHwid(r.hwid)}</td>
                                <td className="mono">{r.beats}</td>
                                <td>{r.lastVerdict ? <VerdictPill verdict={r.lastVerdict} /> : <span className="text-dim">—</span>}</td>
                                <td title={new Date(r.lastSeenAt).toLocaleString()}>{fmtRelative(r.lastSeenAt)}</td>
                                <td className="mono text-dim">{r.ip ?? "—"}</td>
                                <td>
                                    {r.status === "active" && (
                                        <button className="danger" onClick={() => forceEnd(r.id)}>End</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Pager total={data.total} limit={LIMIT} offset={offset} onChange={setOffset} />
            </div>
        </>
    );
}
